import { useMemo, useState } from 'react';
import { DatePicker, Select, Row, Col, Tag, Table, Tooltip, Alert } from 'antd';
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Bar,
  BarChart,
  LineChart,
  Line,
  Tooltip as RechartsTooltip,
  Legend,
  Cell,
} from 'recharts';
import { ClipLoader } from 'react-spinners';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  TrendUp,
  Percent,
  CurrencyCircleDollar,
  CalendarCheck,
  Path,
  Gauge,
  ChartBar,
  ChartLineUp,
  Warning,
  Table as TableIcon,
} from '@phosphor-icons/react';
import dayjs from 'dayjs';
import {
  formatCurrency,
  formatNumberCommasWithDollar,
} from '../../../../../utils/CurrencyUtils';
import { useQuery } from '@tanstack/react-query';
import useApi from '../../../../../utils/AxiosWrapper';
import { useUser } from '../../../../../utils/UserContext';
import { CustomerWithDriverOnboarding } from '../../../../../types/apiTypes';
import { isPreActiveCustomer } from '../home/utils/demoData';
import { DemoDataAlert } from '../home/components/DemoDataAlert';
import { DemoDataWrapper } from '../home/components/DemoDataBadge';
import {
  DriverRevenueData,
  ProfitabilityTrendData,
  DriverMetrics,
  CostBreakdownResponse,
  SummaryMetrics,
  DriverInfo,
  DriverPerformanceProps,
  EXPENSE_CATEGORIES,
  CHART_COLORS,
  LINE_COLORS,
  getDemoSummaryMetrics,
  getDemoDriverMetrics,
  getDemoRevenueVsCostData,
  getDemoProfitabilityTrend,
  getDemoCostBreakdown,
  buildExpenseCategoriesFromMetadata,
} from './driverPerformanceData';

const { RangePicker } = DatePicker;
const { Option } = Select;

function DriverPerformance({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: DriverPerformanceProps) {
  const { callProtectedApi } = useApi();
  const { userRecord, userId } = useUser();
  const [selectedDrivers, setSelectedDrivers] = useState<string[]>(['all']);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(6, 'months'),
    dayjs(),
  ]);

  // Pre-Active Customer Detection
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );

  // Only fetch real data if customer is active
  const shouldFetchRealData = !!userId && !isPreActive;

  // Format dates for API calls
  const startDateStr = dateRange[0]?.format('YYYY-MM-DD') ?? '';
  const endDateStr = dateRange[1]?.format('YYYY-MM-DD') ?? '';

  // API Fetch Functions
  const fetchSummaryMetrics = async (): Promise<SummaryMetrics> => {
    const response = await callProtectedApi(
      `/driverPerformance/summary/${userId}?start_date=${startDateStr}&end_date=${endDateStr}`,
      'get',
      {},
      'Error fetching driver performance summary.'
    );
    return response?.data?.data;
  };

  const fetchDriverMetrics = async (): Promise<DriverMetrics[]> => {
    const response = await callProtectedApi(
      `/driverPerformance/metrics/${userId}?start_date=${startDateStr}&end_date=${endDateStr}`,
      'get',
      {},
      'Error fetching driver metrics.'
    );
    return response?.data?.data ?? [];
  };

  const fetchRevenueVsCostData = async (): Promise<DriverRevenueData[]> => {
    const response = await callProtectedApi(
      `/driverPerformance/revenue-cost-per-mile/${userId}?start_date=${startDateStr}&end_date=${endDateStr}`,
      'get',
      {},
      'Error fetching revenue vs cost data.'
    );
    return response?.data?.data ?? [];
  };

  const fetchCostBreakdown = async (): Promise<CostBreakdownResponse> => {
    const response = await callProtectedApi(
      `/driverPerformance/cost-breakdown/${userId}?start_date=${startDateStr}&end_date=${endDateStr}`,
      'get',
      {},
      'Error fetching cost breakdown data.'
    );
    // Handle the new response shape: { drivers: [...], categoryMetadata: {...} }
    const data = response?.data?.data;
    return {
      drivers: data?.drivers ?? [],
      categoryMetadata: data?.categoryMetadata ?? {},
    };
  };

  const fetchProfitabilityTrend = async (): Promise<
    ProfitabilityTrendData[]
  > => {
    const response = await callProtectedApi(
      `/driverPerformance/profitability-trend/${userId}?start_date=${startDateStr}&end_date=${endDateStr}`,
      'get',
      {},
      'Error fetching profitability trend data.'
    );
    return response?.data?.data ?? [];
  };

  // React Query Hooks
  const {
    data: summaryMetrics,
    isLoading: loadingSummary,
    isError: errorSummary,
    refetch: refetchSummary,
  } = useQuery<SummaryMetrics>({
    queryKey: ['driverPerformanceSummary', userId, startDateStr, endDateStr],
    queryFn: fetchSummaryMetrics,
    enabled: shouldFetchRealData && !!startDateStr && !!endDateStr,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const {
    data: driverMetricsData = [],
    isLoading: loadingMetrics,
    isError: errorMetrics,
    refetch: refetchMetrics,
  } = useQuery<DriverMetrics[]>({
    queryKey: ['driverPerformanceMetrics', userId, startDateStr, endDateStr],
    queryFn: fetchDriverMetrics,
    enabled: shouldFetchRealData && !!startDateStr && !!endDateStr,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const {
    data: revenueVsCostData = [],
    isLoading: loadingRevenueCost,
    isError: errorRevenueCost,
    refetch: refetchRevenueCost,
  } = useQuery<DriverRevenueData[]>({
    queryKey: [
      'driverPerformanceRevenueCost',
      userId,
      startDateStr,
      endDateStr,
    ],
    queryFn: fetchRevenueVsCostData,
    enabled: shouldFetchRealData && !!startDateStr && !!endDateStr,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const {
    data: costBreakdownResponse = { drivers: [], categoryMetadata: {} },
    isLoading: loadingCostBreakdown,
    isError: errorCostBreakdown,
    refetch: refetchCostBreakdown,
  } = useQuery<CostBreakdownResponse>({
    queryKey: [
      'driverPerformanceCostBreakdown',
      userId,
      startDateStr,
      endDateStr,
    ],
    queryFn: fetchCostBreakdown,
    enabled: shouldFetchRealData && !!startDateStr && !!endDateStr,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const {
    data: profitabilityTrendData = [],
    isLoading: loadingTrend,
    isError: errorTrend,
    refetch: refetchTrend,
  } = useQuery<ProfitabilityTrendData[]>({
    queryKey: ['driverPerformanceTrend', userId, startDateStr, endDateStr],
    queryFn: fetchProfitabilityTrend,
    enabled: shouldFetchRealData && !!startDateStr && !!endDateStr,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  // Use demo data for pre-active customers (with fallbacks to prevent "data is not iterable" errors)
  // Wrapped in useMemo to stabilize references for downstream useMemo dependencies
  const displaySummaryMetrics = useMemo(
    () => (isPreActive ? getDemoSummaryMetrics() : summaryMetrics),
    [isPreActive, summaryMetrics]
  );
  const displayDriverMetrics = useMemo(
    () => (isPreActive ? getDemoDriverMetrics() : driverMetricsData) ?? [],
    [isPreActive, driverMetricsData]
  );
  const displayRevenueVsCostData = useMemo(
    () => (isPreActive ? getDemoRevenueVsCostData() : revenueVsCostData) ?? [],
    [isPreActive, revenueVsCostData]
  );
  const displayCostBreakdown = useMemo(
    () =>
      (isPreActive ? getDemoCostBreakdown() : costBreakdownResponse.drivers) ??
      [],
    [isPreActive, costBreakdownResponse.drivers]
  );

  // Build dynamic expense categories from API metadata (for real data)
  // Fall back to static EXPENSE_CATEGORIES for demo/pre-active customers
  const displayExpenseCategories = useMemo(() => {
    if (
      isPreActive ||
      Object.keys(costBreakdownResponse.categoryMetadata).length === 0
    ) {
      return EXPENSE_CATEGORIES.map(({ key, label, color }) => ({
        key,
        label,
        color,
      }));
    }
    return buildExpenseCategoriesFromMetadata(
      costBreakdownResponse.categoryMetadata
    );
  }, [isPreActive, costBreakdownResponse.categoryMetadata]);
  const displayProfitabilityTrend = useMemo(
    () =>
      (isPreActive ? getDemoProfitabilityTrend() : profitabilityTrendData) ??
      [],
    [isPreActive, profitabilityTrendData]
  );

  // Calculate the number of days in the selected date range
  const daysInRange = useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) return 1;
    return Math.max(1, dateRange[1].diff(dateRange[0], 'day') + 1);
  }, [dateRange]);

  // Calculate filtered summary metrics based on selected drivers
  // When specific drivers are selected, aggregate their metrics
  const filteredSummaryMetrics = useMemo((): SummaryMetrics | undefined => {
    // If 'all' is selected, use the original summary metrics
    if (selectedDrivers.includes('all')) {
      return displaySummaryMetrics;
    }

    // Filter driver metrics to only include selected drivers (excluding Average Driver row)
    const filteredDrivers = displayDriverMetrics.filter((driver) => {
      // Skip the Average Driver row - we'll recalculate averages
      if (driver.driverId === null || driver.driver === 'Average Driver') {
        return false;
      }
      return selectedDrivers.includes(driver.driver);
    });

    if (filteredDrivers.length === 0) {
      return displaySummaryMetrics;
    }

    // Calculate aggregated metrics from filtered drivers
    const totalRevenue = filteredDrivers.reduce(
      (sum, d) => sum + d.revenueThisMonth,
      0
    );
    const totalProfit = filteredDrivers.reduce((sum, d) => sum + d.profit, 0);
    const totalMiles = filteredDrivers.reduce(
      (sum, d) => sum + d.milesThisMonth,
      0
    );
    const totalExpenses = totalRevenue - totalProfit;
    const avgProfitMargin =
      totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

    return {
      totalGrossProfit: totalProfit,
      avgProfitMargin,
      totalRevenue,
      totalExpenses,
      totalMiles,
      avgRevenuePerDay: totalRevenue / daysInRange,
      avgMilesPerDay: totalMiles / daysInRange,
      driverCount: filteredDrivers.length,
      // Business activity is not a driver's — it only shows in the all-drivers view
      businessRevenue: 0,
      businessExpenses: 0,
    };
  }, [
    selectedDrivers,
    displayDriverMetrics,
    displaySummaryMetrics,
    daysInRange,
  ]);

  const isLoading =
    !isPreActive &&
    (loadingSummary ||
      loadingMetrics ||
      loadingRevenueCost ||
      loadingCostBreakdown ||
      loadingTrend);

  // Combined error state (only applies when not using demo data)
  const hasAnyError =
    !isPreActive &&
    (errorSummary ||
      errorMetrics ||
      errorRevenueCost ||
      errorCostBreakdown ||
      errorTrend);

  // Refetch all data
  const refetchAll = () => {
    refetchSummary();
    refetchMetrics();
    refetchRevenueCost();
    refetchCostBreakdown();
    refetchTrend();
  };

  // Check if there's no driver data (for empty state messaging)
  const hasNoDriverData =
    !isPreActive &&
    !isLoading &&
    !hasAnyError &&
    displayDriverMetrics.length === 0;

  // Empty State Component
  const EmptyState = ({
    title,
    description,
    icon,
  }: {
    title: string;
    description: string;
    icon?: React.ReactNode;
  }) => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="mb-4 rounded-full bg-gray-100 p-4">
        {icon || (
          <ChartBar size={32} weight="duotone" className="text-gray-400" />
        )}
      </div>
      <h3 className="text-base font-medium text-gray-900">{title}</h3>
      <p className="mt-1 max-w-sm text-sm text-gray-500">{description}</p>
    </div>
  );

  // Error State Component
  const ErrorState = ({
    message,
    onRetry,
  }: {
    message: string;
    onRetry?: () => void;
  }) => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="mb-4 rounded-full bg-red-50 p-4">
        <Warning size={32} weight="duotone" className="text-red-400" />
      </div>
      <h3 className="text-base font-medium text-gray-900">
        Unable to load data
      </h3>
      <p className="mt-1 max-w-sm text-sm text-gray-500">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-4 text-sm font-medium text-primary underline hover:text-primary/80"
        >
          Try again
        </button>
      )}
    </div>
  );

  // Loading State Component
  const LoadingState = ({ height = 350 }: { height?: number }) => (
    <div
      className="flex items-center justify-center"
      style={{ height: `${height}px` }}
    >
      <ClipLoader color="#1D213B" size={32} />
    </div>
  );

  // Extract driver list for dropdown
  const driverList: DriverInfo[] = useMemo(() => {
    return displayDriverMetrics
      .filter((d) => d.driverId !== null)
      .map((d) => ({
        driverId: d.driverId!,
        name: d.driver,
      }));
  }, [displayDriverMetrics]);

  // Filter data based on selected drivers
  // Build a set of selected driver IDs for filtering (maps full names to IDs)
  const selectedDriverIds = useMemo(() => {
    const ids = new Set<string>();
    driverList.forEach((d) => {
      if (selectedDrivers.includes(d.name)) {
        ids.add(d.driverId);
      }
    });
    return ids;
  }, [selectedDrivers, driverList]);

  const getFilteredData = <
    T extends { driver?: string; fullName?: string; driverId?: string | null },
  >(
    data: T[]
  ): T[] => {
    if (!data || !Array.isArray(data)) return [];
    if (selectedDrivers.includes('all')) return data;

    return data.filter((item) => {
      // Check for Average Driver
      const isAverageDriver =
        item.driver === 'Average Driver' ||
        item.fullName === 'Average Driver' ||
        item.driver === 'Average' ||
        item.driverId === null;

      if (selectedDrivers.includes('Average Driver') && isAverageDriver) {
        return true;
      }

      // Check by driver name or fullName
      if (
        selectedDrivers.includes(item.driver ?? '') ||
        selectedDrivers.includes(item.fullName ?? '')
      ) {
        return true;
      }

      // Check by driverId (handles cases where driver field uses short names)
      if (item.driverId && selectedDriverIds.has(item.driverId)) {
        return true;
      }

      return false;
    });
  };

  // Helper to check if a row is the Average Driver (benchmark row)
  const isAverageDriverRow = (record: DriverMetrics) =>
    record.driverId === null;

  // Custom X-axis tick renderer for charts - highlights the Average/Benchmark label
  const CustomXAxisTick = ({ x, y, payload }: any) => {
    const isAverage = payload.value === 'Average';

    if (isAverage) {
      return (
        <g transform={`translate(${x},${y})`}>
          <rect x={-38} y={2} width={76} height={22} rx={4} fill="#1D213B" />
          <text
            x={0}
            y={17}
            textAnchor="middle"
            fill="#ffffff"
            fontSize={11}
            fontWeight={600}
          >
            ★ Fleet Avg
          </text>
        </g>
      );
    }

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={16} textAnchor="middle" fill="#666" fontSize={12}>
          {payload.value}
        </text>
      </g>
    );
  };

  // Table columns for driver scorecard
  const scorecardColumns = [
    {
      title: 'Driver',
      dataIndex: 'driver',
      key: 'driver',
      fixed: 'left' as const,
      width: 200,
      render: (text: string, record: DriverMetrics) => {
        if (isAverageDriverRow(record)) {
          return (
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded bg-[#1D213B]">
                <ChartBar size={14} weight="fill" className="text-white" />
              </div>
              <div className="flex flex-col">
                <span className="font-semibold text-[#1D213B]">
                  Fleet Average
                </span>
                <span className="text-[11px] italic text-gray-500">
                  Benchmark (not included in totals)
                </span>
              </div>
            </div>
          );
        }
        return <span className="font-medium">{text}</span>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: DriverMetrics) => {
        if (isAverageDriverRow(record)) {
          return (
            <Tag color="purple" className="border-dashed">
              Benchmark
            </Tag>
          );
        }
        return (
          <Tag color={status === 'Active' ? 'green' : 'default'}>{status}</Tag>
        );
      },
    },
    {
      title: 'Profit Margin',
      dataIndex: 'profitMargin',
      key: 'profitMargin',
      sorter: (a: DriverMetrics, b: DriverMetrics) =>
        a.profitMargin - b.profitMargin,
      render: (value: number, record: DriverMetrics) => (
        <span
          className={`${isAverageDriverRow(record) ? 'font-semibold' : ''}${value > 20 ? 'text-green-600' : value > 10 ? 'text-yellow-600' : 'text-red-600'}`}
        >
          {value.toFixed(1)}%
        </span>
      ),
    },
    {
      title: 'Profit',
      dataIndex: 'profit',
      key: 'profit',
      sorter: (a: DriverMetrics, b: DriverMetrics) => a.profit - b.profit,
      render: (value: number, record: DriverMetrics) => (
        <span className={isAverageDriverRow(record) ? 'font-semibold' : ''}>
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      title: 'Miles Driven',
      dataIndex: 'milesThisMonth',
      key: 'milesThisMonth',
      sorter: (a: DriverMetrics, b: DriverMetrics) =>
        a.milesThisMonth - b.milesThisMonth,
      render: (value: number, record: DriverMetrics) => (
        <span className={isAverageDriverRow(record) ? 'font-semibold' : ''}>
          {value.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Total Revenue',
      dataIndex: 'revenueThisMonth',
      key: 'revenueThisMonth',
      sorter: (a: DriverMetrics, b: DriverMetrics) =>
        a.revenueThisMonth - b.revenueThisMonth,
      render: (value: number, record: DriverMetrics) => (
        <span className={isAverageDriverRow(record) ? 'font-semibold' : ''}>
          {formatCurrency(value)}
        </span>
      ),
    },
  ];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="rounded-lg bg-gray-800 p-3 text-white shadow-lg">
          <p className="mb-2 text-[15px] font-semibold text-white">{label}</p>
          {payload.map((entry: any, index: number) => {
            // Determine color for tooltip
            let displayColor = entry.color || entry.fill;

            // For Profit/Mile: bright green by default, dark red only when negative
            if (entry.name === 'Profit/Mile') {
              displayColor =
                entry.value < 0 ? CHART_COLORS.dark_red : CHART_COLORS.success;
            }

            return (
              <div key={index} className="mb-1 flex items-center gap-2">
                <div
                  className="h-4 w-4 rounded"
                  style={{ backgroundColor: displayColor }}
                />
                <p className="text-[14px] text-white">
                  {entry.name}:{' '}
                  {typeof entry.value === 'number'
                    ? entry.name.includes('Mile') ||
                      entry.name.includes('Cost') ||
                      entry.name.includes('Revenue')
                      ? `$${entry.value.toFixed(2)}`
                      : entry.value.toFixed(1)
                    : entry.value}
                </p>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <style>{`
        /* Average Driver benchmark row styling */
        .average-driver-row {
          background-color: #F0F4FF !important;
        }
        .average-driver-row td {
          background-color: #F0F4FF !important;
          border-bottom: 2px solid #D1D5DB !important;
        }
        .average-driver-row:hover td {
          background-color: #E8EDFF !important;
        }

        @media (max-width: 640px) {
          .ant-picker-dropdown {
            max-width: calc(100vw - 48px) !important;
            overflow: hidden !important;
          }
          .ant-picker-dropdown .ant-picker-panel-container {
            overflow-x: auto !important;
            max-width: calc(100vw - 48px) !important;
          }
          .ant-picker-dropdown .ant-picker-panels {
            display: flex !important;
            flex-wrap: nowrap !important;
            width: max-content !important;
          }
          .ant-picker-dropdown .ant-picker-panel {
            min-width: 280px !important;
          }
        }
      `}</style>
      <div className="flex h-full w-[95%] flex-col overflow-y-auto overflow-x-hidden pb-4 md:w-full md:px-4 lg:w-[1148px] lg:max-w-[1148px] lg:px-0">
        {/* Demo Data Alert for Pre-Active Customers */}
        {isPreActive && <DemoDataAlert />}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <ClipLoader color="#1D213B" size={40} />
          </div>
        )}

        {/* Global Error Alert */}
        {hasAnyError && (
          <Alert
            message="Error loading driver performance data"
            description="Some data could not be loaded. Please try refreshing the page or selecting a different date range."
            type="error"
            showIcon
            className="mb-4"
            action={
              <button
                onClick={refetchAll}
                className="text-sm font-medium text-red-600 underline hover:text-red-800"
              >
                Retry
              </button>
            }
          />
        )}

        {/* No Driver Data Info Alert */}
        {hasNoDriverData && (
          <Alert
            message="No driver activity found"
            description="No drivers have recorded transactions in this date range. Try selecting a different period or ensure your drivers have completed trips."
            type="info"
            showIcon
            className="mb-4"
          />
        )}

        {/* Header and Filters */}
        <div className="sticky top-0 z-10 w-full border-b-[1px] border-bordergray bg-primarybg px-4 py-4 lg:bg-white">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
            <RangePicker
              value={dateRange}
              onChange={(values) =>
                values && setDateRange(values as [dayjs.Dayjs, dayjs.Dayjs])
              }
              format="MM/DD/YYYY"
              className="min-w-[240px]"
            />
            <Select
              mode="multiple"
              placeholder="Select drivers"
              value={selectedDrivers}
              onChange={setSelectedDrivers}
              style={{ minWidth: 200 }}
              maxTagCount={2}
              className="min-w-[200px]"
            >
              <Option value="all">All Drivers</Option>
              <Option value="Average Driver" style={{ fontWeight: 600 }}>
                Average Driver
              </Option>
              {driverList.map((driver) => (
                <Option key={driver.driverId} value={driver.name}>
                  {driver.name}
                </Option>
              ))}
            </Select>
          </div>
        </div>

        {/* Summary Metrics */}
        {/* One ribbon for the whole tile block rather than six — the tiles read
            as a single unit, and six badges would be noise. */}
        <DemoDataWrapper showBadge={isPreActive}>
          <div className="flex w-full flex-col gap-[16px] pb-6 pt-4">
            {/* Row 1: Profit, Margin, Revenue */}
            <div className="flex w-full flex-col items-stretch gap-[16px] lg:flex-row">
              {/* Total Gross Profit */}
              <div className="flex min-h-[100px] w-full flex-row items-center gap-x-[16px] border-[1px] border-bordergray bg-white p-4 lg:flex-1">
                <div className="rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7] p-[12px]">
                  <TrendUp
                    size={28}
                    weight="duotone"
                    className="text-secondary"
                  />
                </div>
                <div className="flex flex-col items-start">
                  <h2 className="text-[14px] text-[#0A112F]">
                    Total Gross Profit
                  </h2>
                  <h1 className="text-[26px] font-[700] leading-[34px] text-secondary">
                    {formatNumberCommasWithDollar(
                      Math.floor(filteredSummaryMetrics?.totalGrossProfit ?? 0)
                    )}
                  </h1>
                </div>
              </div>

              {/* Avg Profit Margin */}
              <div className="flex min-h-[100px] w-full flex-row items-center gap-x-[16px] border-[1px] border-bordergray bg-white p-4 lg:flex-1">
                <div className="rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7] p-[12px]">
                  <Percent
                    size={28}
                    weight="duotone"
                    className="text-primary"
                  />
                </div>
                <div className="flex flex-col items-start">
                  <h2 className="text-[14px] text-[#0A112F]">
                    Avg Profit Margin
                  </h2>
                  <h1
                    className={`text-[26px] font-[700] leading-[34px] ${(filteredSummaryMetrics?.avgProfitMargin ?? 0) > 15 ? 'text-secondary' : 'text-warning'}`}
                  >
                    {(filteredSummaryMetrics?.avgProfitMargin ?? 0).toFixed(1)}%
                  </h1>
                </div>
              </div>

              {/* Total Revenue */}
              <div className="flex min-h-[100px] w-full flex-row items-center gap-x-[16px] border-[1px] border-bordergray bg-white p-4 lg:flex-1">
                <div className="rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7] p-[12px]">
                  <CurrencyCircleDollar
                    size={28}
                    weight="duotone"
                    className="text-primary"
                  />
                </div>
                <div className="flex flex-col items-start">
                  <h2 className="text-[14px] text-[#0A112F]">Total Revenue</h2>
                  <h1 className="text-[26px] font-[700] leading-[34px] text-[#1D213B]">
                    {formatNumberCommasWithDollar(
                      Math.floor(filteredSummaryMetrics?.totalRevenue ?? 0)
                    )}
                  </h1>
                  {/* Business = money not assigned to any truck. Shown as an
                    explicit line item with a value-framed nudge to attribute.
                    Business is a valid resting state, so this encourages
                    (opt-in), it does not warn. */}
                  {(filteredSummaryMetrics?.businessRevenue ?? 0) > 0 ||
                  (filteredSummaryMetrics?.businessExpenses ?? 0) > 0 ? (
                    <>
                      <p className="text-[12px] text-graysecondary">
                        Business:{' '}
                        {formatNumberCommasWithDollar(
                          Math.floor(
                            filteredSummaryMetrics?.businessRevenue ?? 0
                          )
                        )}{' '}
                        revenue ·{' '}
                        {formatNumberCommasWithDollar(
                          Math.floor(
                            filteredSummaryMetrics?.businessExpenses ?? 0
                          )
                        )}{' '}
                        expenses
                      </p>
                      <p className="text-[11px] text-graysecondary">
                        Assign transactions to trucks to see per-truck
                        profitability.
                      </p>
                    </>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Row 2: Avg Daily Revenue, Total Miles, Avg Daily Miles */}
            <div className="flex w-full flex-col items-stretch gap-[16px] lg:flex-row">
              {/* Avg Daily Revenue */}
              <div className="flex min-h-[100px] w-full flex-row items-center gap-x-[16px] border-[1px] border-bordergray bg-white p-4 lg:flex-1">
                <div className="rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7] p-[12px]">
                  <CalendarCheck
                    size={28}
                    weight="duotone"
                    className="text-primary"
                  />
                </div>
                <div className="flex flex-col items-start">
                  <h2 className="text-[14px] text-[#0A112F]">
                    Avg Daily Revenue
                  </h2>
                  <h1 className="text-[26px] font-[700] leading-[34px] text-[#1D213B]">
                    {formatNumberCommasWithDollar(
                      Math.floor(filteredSummaryMetrics?.avgRevenuePerDay ?? 0)
                    )}
                  </h1>
                </div>
              </div>

              {/* Total Miles */}
              <div className="flex min-h-[100px] w-full flex-row items-center gap-x-[16px] border-[1px] border-bordergray bg-white p-4 lg:flex-1">
                <div className="rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7] p-[12px]">
                  <Path size={28} weight="duotone" className="text-primary" />
                </div>
                <div className="flex flex-col items-start">
                  <h2 className="text-[14px] text-[#0A112F]">Total Miles</h2>
                  <h1 className="text-[26px] font-[700] leading-[34px] text-[#1D213B]">
                    {(filteredSummaryMetrics?.totalMiles ?? 0).toLocaleString()}
                  </h1>
                </div>
              </div>

              {/* Avg Daily Miles */}
              <div className="flex min-h-[100px] w-full flex-row items-center gap-x-[16px] border-[1px] border-bordergray bg-white p-4 lg:flex-1">
                <div className="rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7] p-[12px]">
                  <Gauge size={28} weight="duotone" className="text-primary" />
                </div>
                <div className="flex flex-col items-start">
                  <h2 className="text-[14px] text-[#0A112F]">
                    Avg Daily Miles
                  </h2>
                  <h1 className="text-[26px] font-[700] leading-[34px] text-[#1D213B]">
                    {Math.round(
                      filteredSummaryMetrics?.avgMilesPerDay ?? 0
                    ).toLocaleString()}
                  </h1>
                </div>
              </div>
            </div>
          </div>
        </DemoDataWrapper>

        {/* Main Charts Section */}
        <Row gutter={[16, 16]}>
          {/* Revenue vs Cost per Mile Comparison */}
          <Col xs={24}>
            <DemoDataWrapper showBadge={isPreActive}>
              <div className="flex flex-col border-[1px] border-bordergray bg-white">
                <div className="flex h-[71px] min-h-[71px] items-center justify-start border-b-[1px] border-bordergray p-4">
                  <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                    Revenue vs Cost per Mile by Driver
                  </h2>
                </div>
                <div className="p-4">
                  {!isPreActive && loadingRevenueCost ? (
                    <LoadingState height={350} />
                  ) : errorRevenueCost ? (
                    <ErrorState
                      message="Failed to load revenue and cost data. Please try again."
                      onRetry={refetchRevenueCost}
                    />
                  ) : getFilteredData(displayRevenueVsCostData).length === 0 ? (
                    <EmptyState
                      title="No revenue data available"
                      description="There's no revenue or mileage data for the selected date range. Try selecting a different period or ensure drivers have completed trips."
                      icon={
                        <ChartBar
                          size={32}
                          weight="duotone"
                          className="text-gray-400"
                        />
                      }
                    />
                  ) : (
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart
                        data={getFilteredData(displayRevenueVsCostData)}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="driver"
                          tick={<CustomXAxisTick />}
                          height={45}
                        />
                        <YAxis
                          label={{
                            value: 'Cost / Revenue per Mile ($)',
                            angle: -90,
                            position: 'insideLeft',
                          }}
                          tickFormatter={(value) => value.toFixed(2)}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ paddingTop: '12px' }} />
                        <Bar
                          dataKey="revenuePerMile"
                          name="Revenue/Mile"
                          fill={CHART_COLORS.secondary}
                        />
                        <Bar
                          dataKey="costPerMile"
                          name="Cost/Mile"
                          fill={CHART_COLORS.danger}
                        />
                        <Bar dataKey="profitPerMile" name="Profit/Mile">
                          {getFilteredData(displayRevenueVsCostData).map(
                            (entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={
                                  entry.profitPerMile < 0
                                    ? CHART_COLORS.dark_red
                                    : CHART_COLORS.success
                                }
                              />
                            )
                          )}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </DemoDataWrapper>
          </Col>

          {/* Driver Profitability Over Time */}
          <Col xs={24}>
            <DemoDataWrapper showBadge={isPreActive}>
              <div className="flex flex-col border-[1px] border-bordergray bg-white">
                <div className="flex h-[71px] min-h-[71px] items-center justify-between border-b-[1px] border-bordergray p-4">
                  <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                    Driver Profitability Over Time
                  </h2>
                  <Tooltip title="Monthly profit margin percentage trends for each driver">
                    <InfoCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </div>
                <div className="p-4">
                  {!isPreActive && loadingTrend ? (
                    <LoadingState height={350} />
                  ) : errorTrend ? (
                    <ErrorState
                      message="Failed to load profitability trend data. Please try again."
                      onRetry={refetchTrend}
                    />
                  ) : displayProfitabilityTrend.length === 0 ||
                    driverList.length === 0 ? (
                    <EmptyState
                      title="No profitability trends available"
                      description="There's no monthly profitability data for the selected date range. Trends will appear once drivers have recorded transactions over multiple months."
                      icon={
                        <ChartLineUp
                          size={32}
                          weight="duotone"
                          className="text-gray-400"
                        />
                      }
                    />
                  ) : (
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={displayProfitabilityTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          label={{
                            value: 'Month',
                            position: 'insideBottom',
                            offset: -5,
                          }}
                        />
                        <YAxis
                          domain={[0, 50]}
                          label={{
                            value: 'Profit Margin (%)',
                            angle: -90,
                            position: 'insideLeft',
                          }}
                          tickFormatter={(value) => `${value}%`}
                        />
                        <RechartsTooltip
                          content={({ active, payload, label }: any) => {
                            if (active && payload && payload.length) {
                              return (
                                <div className="rounded-lg bg-gray-800 p-3 text-white shadow-lg">
                                  <p className="mb-2 text-[15px] font-semibold text-white">
                                    Month: {label}
                                  </p>
                                  {payload.map((entry: any, index: number) => (
                                    <div
                                      key={index}
                                      className="mb-1 flex items-center gap-2"
                                    >
                                      <div
                                        className="h-4 w-4 rounded"
                                        style={{
                                          backgroundColor:
                                            entry.color || entry.stroke,
                                          border:
                                            entry.name === 'Average Driver'
                                              ? '1px solid white'
                                              : undefined,
                                        }}
                                      />
                                      <p className="text-[14px] text-white">
                                        {entry.name}: {entry.value?.toFixed(1)}%
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend wrapperStyle={{ paddingTop: '12px' }} />
                        {/* Render Average Driver line if "all" is selected or specifically selected */}
                        {(selectedDrivers.includes('all') ||
                          selectedDrivers.includes('Average Driver')) && (
                          <Line
                            key="avg"
                            type="monotone"
                            dataKey="Average Driver"
                            stroke={CHART_COLORS.primary}
                            strokeWidth={3}
                            strokeDasharray="5 5"
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                        )}
                        {/* Render individual driver lines */}
                        {driverList
                          .filter(
                            (driver) =>
                              selectedDrivers.includes('all') ||
                              selectedDrivers.includes(driver.name)
                          )
                          .map((driver, index) => (
                            <Line
                              key={driver.driverId}
                              type="monotone"
                              dataKey={driver.name}
                              stroke={LINE_COLORS[index % LINE_COLORS.length]}
                              strokeWidth={2}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                            />
                          ))}
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </DemoDataWrapper>
          </Col>

          {/* Driver Performance Scorecard */}
          <Col xs={24}>
            <DemoDataWrapper showBadge={isPreActive}>
              <div className="flex flex-col border-[1px] border-bordergray bg-white">
                <div className="flex h-[71px] min-h-[71px] items-center justify-start border-b-[1px] border-bordergray p-4">
                  <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                    Driver Performance Scorecard
                  </h2>
                </div>
                <div className="p-4">
                  {!isPreActive && loadingMetrics ? (
                    <LoadingState height={300} />
                  ) : errorMetrics ? (
                    <ErrorState
                      message="Failed to load driver metrics. Please try again."
                      onRetry={refetchMetrics}
                    />
                  ) : getFilteredData(displayDriverMetrics).length === 0 ? (
                    <EmptyState
                      title="No driver performance data"
                      description="There are no driver metrics available for the selected date range. Data will appear once drivers have recorded trips and transactions."
                      icon={
                        <TableIcon
                          size={32}
                          weight="duotone"
                          className="text-gray-400"
                        />
                      }
                    />
                  ) : (
                    <Table
                      columns={scorecardColumns}
                      dataSource={getFilteredData(displayDriverMetrics)}
                      rowKey={(record) => record.driverId ?? record.driver}
                      rowClassName={(record) =>
                        isAverageDriverRow(record) ? 'average-driver-row' : ''
                      }
                      pagination={{ pageSize: 10 }}
                      scroll={{ x: 1200 }}
                      size="small"
                    />
                  )}
                </div>
              </div>
            </DemoDataWrapper>
          </Col>

          {/* Cost Breakdown by Category */}
          <Col xs={24}>
            <DemoDataWrapper showBadge={isPreActive}>
              <div className="flex flex-col border-[1px] border-bordergray bg-white">
                <div className="flex h-[71px] min-h-[71px] items-center justify-start border-b-[1px] border-bordergray p-4">
                  <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                    Cost Breakdown by Category
                  </h2>
                </div>
                <div className="p-4">
                  {!isPreActive && loadingCostBreakdown ? (
                    <LoadingState height={300} />
                  ) : errorCostBreakdown ? (
                    <ErrorState
                      message="Failed to load cost breakdown data. Please try again."
                      onRetry={refetchCostBreakdown}
                    />
                  ) : getFilteredData(displayCostBreakdown).length === 0 ? (
                    <EmptyState
                      title="No expense data available"
                      description="There are no expense transactions categorized for the selected date range. Cost breakdowns will appear once expenses are recorded."
                      icon={
                        <ChartBar
                          size={32}
                          weight="duotone"
                          className="text-gray-400"
                        />
                      }
                    />
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={getFilteredData(displayCostBreakdown)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="driver"
                          tick={<CustomXAxisTick />}
                          height={45}
                        />
                        <YAxis
                          tickFormatter={(value) => value.toLocaleString()}
                          label={{
                            value: 'Cost ($)',
                            angle: -90,
                            position: 'insideLeft',
                          }}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ paddingTop: '12px' }} />
                        {displayExpenseCategories.map(
                          ({ key, label, color }) => (
                            <Bar
                              key={key}
                              dataKey={key}
                              stackId="a"
                              fill={color}
                              name={label}
                            />
                          )
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </DemoDataWrapper>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default DriverPerformance;
